import { useCallback, useState } from 'react'
import { useInnskraningSentryLogging } from './useInnskraningExceptionCapture'
import {
  InnskraningAnalyticsStepEnum,
  trackInnskraningEvent,
} from 'utils/onboarding/analytics'
import { AddressPicker } from 'onboarding/components/addresspicker/AddressPicker'

type PropertyOfOtherKind = {
  selected: boolean
  propertyNumber: string
  apartmentNumber: string
  size: string | number
}

type SearchResults = Array<{
  address: string
  postalCode: string
  neighborhood: string
  properties: Array<PropertyOfOtherKind>
}>

export const useAddressSearch = () => {
  const [state, _setState] = useState({
    loading: false,
    searchQuery: '',
    searchResults: [] as SearchResults,
    noResults: false,
    error: false,
    errorMessage: '',
  })

  const [isLoading, setLoading] = useState(false)

  const setState = useCallback((newState: Partial<typeof state>) => {
    _setState((prevState) => ({
      ...prevState,
      ...newState,
    }))
  }, [])

  const { captureException } = useInnskraningSentryLogging()

  const search = () => {
    trackInnskraningEvent(
      InnskraningAnalyticsStepEnum.ADD_ADDRESS_SEARCH_BUTTON_CLICK
    )

    const query = state.searchQuery

    if (!query.length) {
      setState({
        error: true,
        errorMessage: 'Vinsamlegast sláðu inn heimilisfang',
      })
      return
    } else if (state.error) {
      setState({
        error: false,
        errorMessage: '',
      })
    }

    setState({
      noResults: false,
      searchResults: [],
      loading: true,
    })

    setLoading(true)

    fetch(`/api/onboarding/property-search?address=${query}`, {
      method: 'GET',
    })
      .then((res) => {
        if (res.status >= 400) {
          console.error(res)
          throw new Error()
        }
        return res.json()
      })
      .then((res) => {
        setState({
          searchResults: res,
          error: false,
          errorMessage: '',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          noResults: res.length === 0,
        })
      })
      .catch((e) => {
        captureException(e, () => ({
          tags: {
            component: AddressPicker.name,
          },
          contexts: {
            error: {
              message:
                'Eitthvað mistókst í leitinni, vinsamlegast reynið aftur',
            },
          },
        }))

        setState({
          error: true,
          errorMessage:
            'Eitthvað mistókst í leitinni, vinsamlegast reynið aftur.',
        })
      })
      .finally(() => {
        setState({ loading: false })
        setLoading(false)
      })
  }

  return {
    state,
    search,
    setState,
    isLoading,
  }
}
